/**
 * Shared types for Contests feature (Employee Motivation)
 *
 * Contests are company-level competitions that incentivize employee performance
 * across metrics like punctuality, sales, and tips.
 */
import { z } from "zod";
// ===== ENUM SCHEMAS =====
export const ContestStatusSchema = z.enum([
    "CREATED",
    "IN_PROCESS",
    "COMPLETED",
    "ARCHIVED",
    "TERMINATED",
]);
export const ContestTypeSchema = z.enum([
    "LEADERBOARD",
    "THRESHOLD",
    "STREAK",
    "PERSONAL_BEST",
]);
export const ContestMetricSchema = z.enum([
    "PUNCTUALITY",
    "SALES",
    "TIPS_PERCENT",
]);
export const ContestScopeTypeSchema = z.enum(["INDIVIDUAL", "LOCATION"]);
export const ContestRewardTypeSchema = z.enum(["PTO_CREDIT", "MANAGER_TASK", "CASH_APP_BONUS", "GIFT_CARD", "RECOGNITION", "OTHER"]);
// ===== CONFIG SCHEMAS =====
/**
 * Discriminated union for ContestConfig by ContestType
 */
export const ContestConfigSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("LEADERBOARD"),
        config: z.object({ winnerCount: z.number().int().min(1) }),
    }),
    z.object({
        type: z.literal("THRESHOLD"),
        config: z.object({ targetValue: z.number().positive() }),
    }),
    z.object({
        type: z.literal("STREAK"),
        config: z.object({
            minimumStreak: z.number().int().min(1),
            dailyTarget: z.number().positive(),
        }),
    }),
    z.object({
        type: z.literal("PERSONAL_BEST"),
        config: z.object({ improvementPercent: z.number().positive() }),
    }),
]);
/**
 * Discriminated union for RewardConfig by ContestRewardType
 */
export const ContestRewardConfigSchema = z.discriminatedUnion("rewardType", [
    z.object({
        rewardType: z.literal("PTO_CREDIT"),
        rewardConfig: z.object({ hours: z.number().positive() }),
    }),
    z.object({
        rewardType: z.literal("MANAGER_TASK"),
        rewardConfig: z.object({ taskDescription: z.string().min(1) }),
    }),
    z.object({
        rewardType: z.literal("CASH_APP_BONUS"),
        rewardConfig: z.object({ amount: z.number().positive() }),
    }),
    z.object({
        rewardType: z.literal("GIFT_CARD"),
        rewardConfig: z.object({ amount: z.number().positive(), vendor: z.string().optional() }),
    }),
    z.object({
        rewardType: z.literal("RECOGNITION"),
        rewardConfig: z.object({ message: z.string().optional() }),
    }),
    z.object({
        rewardType: z.literal("OTHER"),
        rewardConfig: z.object({ description: z.string().min(1) }),
    }),
]);
// ===== INPUT SCHEMAS =====
/**
 * Create contest input
 */
export const CreateContestInputSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, "Name is required")
        .max(255, "Name must be 255 characters or less"),
    description: z.string().optional(),
    type: ContestTypeSchema,
    metric: ContestMetricSchema,
    scopeType: ContestScopeTypeSchema,
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    baselineStartDate: z.iso.datetime().optional(),
    baselineEndDate: z.iso.datetime().optional(),
    config: z.record(z.string(), z.unknown()).default({}),
    rewardType: ContestRewardTypeSchema,
    rewardConfig: z.record(z.string(), z.unknown()).default({}),
    locationIds: z.array(z.uuid()).default([]),
    jobRoleFamilyIds: z.array(z.uuid()).optional(),
    employmentIds: z.array(z.uuid()).optional(),
});
/**
 * Update contest input (partial)
 */
export const UpdateContestInputSchema = z.object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().optional(),
    type: ContestTypeSchema.optional(),
    metric: ContestMetricSchema.optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    rewardType: ContestRewardTypeSchema.optional(),
    rewardConfig: z.record(z.string(), z.unknown()).optional(),
    locationIds: z.array(z.uuid()).optional(),
    jobRoleFamilyIds: z.array(z.uuid()).optional(),
    employmentIds: z.array(z.uuid()).optional(),
});
/**
 * List query filters
 */
export const ListContestsQuerySchema = z.object({
    status: ContestStatusSchema.optional(),
    metric: ContestMetricSchema.optional(),
    page: z.coerce.number().int().min(1).optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
/**
 * Approve winners input
 */
export const ApproveWinnersInputSchema = z.object({
    id: z.uuid(),
    winnerIds: z.array(z.uuid()).default([]),
});
// ===== RESPONSE SCHEMAS =====
/**
 * Contest response schema
 */
export const ContestResponseSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    status: ContestStatusSchema,
    type: ContestTypeSchema,
    metric: ContestMetricSchema,
    scopeType: ContestScopeTypeSchema,
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    baselineStartDate: z.iso.datetime().nullable(),
    baselineEndDate: z.iso.datetime().nullable(),
    config: z.record(z.string(), z.unknown()),
    rewardType: ContestRewardTypeSchema,
    rewardConfig: z.record(z.string(), z.unknown()),
    locationIds: z.array(z.uuid()),
    jobRoleFamilyIds: z.array(z.uuid()).nullable(),
    employmentIds: z.array(z.uuid()).nullable(),
    createdById: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * List contests response
 */
export const ListContestsResponseSchema = z.object({
    data: z.array(ContestResponseSchema),
    total: z.number().int().nonnegative(),
});
// ===== DASHBOARD SCHEMAS =====
/**
 * Compact contest card for the employee dashboard.
 */
export const ContestDashboardCardSchema = z.object({
    contestId: z.uuid(),
    name: z.string(),
    metric: ContestMetricSchema,
    scopeType: ContestScopeTypeSchema,
    status: ContestStatusSchema,
    userScore: z.number().nullable(),
    userRank: z.number().int().positive().nullable(),
    totalParticipants: z.number().int().nonnegative(),
});
export const ContestDashboardResponseSchema = z.array(ContestDashboardCardSchema);
// ===== AUDIT LOG SCHEMAS =====
export const ContestAuditActionSchema = z.enum([
    "CREATED",
    "STARTED",
    "UPDATED",
    "ENDED_EARLY",
    "COMPLETED",
    "ARCHIVED",
    "TERMINATED",
    "WINNER_SELECTED",
    "WINNER_APPROVED",
    "REWARD_GRANTED",
    "NOTIFICATION_SENT",
    "PARTICIPANT_ADDED",
    "PARTICIPANT_REMOVED",
    "SCORE_UPDATED",
]);
export const AuditLogQuerySchema = z.object({
    action: ContestAuditActionSchema.optional(),
    page: z.coerce.number().int().min(1).optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export const AuditLogEntrySchema = z.object({
    id: z.uuid(),
    contestId: z.uuid(),
    action: ContestAuditActionSchema,
    actorId: z.uuid().nullable(),
    details: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.iso.datetime(),
});
export const AuditLogPageSchema = z.object({
    data: z.array(AuditLogEntrySchema),
    total: z.number().int().nonnegative(),
});
// ===== SCORE / LEADERBOARD SCHEMAS =====
/**
 * A single leaderboard entry with rank and score details
 */
export const LeaderboardEntrySchema = z.object({
    rank: z.number().int().positive(),
    scoreId: z.uuid(),
    contestId: z.uuid(),
    employmentId: z.uuid().nullable(),
    locationId: z.uuid(),
    computedScore: z.number(),
    currentValue: z.number(),
    baselineValue: z.number().nullable(),
    currentStreak: z.number().int().nonnegative(),
    bestStreak: z.number().int().nonnegative(),
    lastSyncAt: z.iso.datetime(),
    participantName: z.string().nullable().optional(),
});
/**
 * Leaderboard response
 */
export const LeaderboardResponseSchema = z.object({
    contestId: z.uuid(),
    scopeType: ContestScopeTypeSchema,
    entries: z.array(LeaderboardEntrySchema),
});
/**
 * A single raw contest score record
 */
export const ContestScoreSchema = z.object({
    id: z.uuid(),
    contestId: z.uuid(),
    employmentId: z.uuid().nullable(),
    locationId: z.uuid(),
    currentValue: z.number(),
    baselineValue: z.number().nullable(),
    computedScore: z.number(),
    currentStreak: z.number().int().nonnegative(),
    bestStreak: z.number().int().nonnegative(),
    lastSyncAt: z.iso.datetime(),
    syncSource: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Scores list response
 */
export const ContestScoresResponseSchema = z.array(ContestScoreSchema);
// ===== WINNER SCHEMAS =====
/**
 * A single contest winner record
 */
export const ContestWinnerSchema = z.object({
    id: z.uuid(),
    contestId: z.uuid(),
    employmentId: z.uuid(),
    locationId: z.uuid(),
    rank: z.number().int().positive(),
    finalScore: z.number(),
    rewardGranted: z.boolean(),
    rewardGrantedAt: z.iso.datetime().nullable(),
    rewardDetails: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.iso.datetime(),
});
export const ContestWinnersResponseSchema = z.array(ContestWinnerSchema);
// ===== NOTIFICATION PLAN SCHEMAS =====
export const ContestNotificationTypeSchema = z.enum([
    "FIXED_DATE",
    "RELATIVE_DATE",
    "RECURRING",
]);
export const FixedDateConfigSchema = z.object({
    sendAt: z.iso.datetime(),
});
export const RelativeDateConfigSchema = z.object({
    offsetDays: z.number().int(),
    anchor: z.enum(["start", "end"]),
});
export const RecurringConfigSchema = z.object({
    intervalDays: z.number().int().positive(),
    startOffset: z.number().int().nonnegative().default(0),
});
export const NotificationScheduleConfigSchema = z.union([
    FixedDateConfigSchema,
    RelativeDateConfigSchema,
    RecurringConfigSchema,
]);
export const CreateNotificationPlanInputSchema = z.object({
    type: ContestNotificationTypeSchema,
    title: z.string().trim().min(1).max(255),
    body: z.string().trim().min(1),
    channels: z.array(z.string()).optional(),
    scheduleConfig: z.record(z.string(), z.unknown()),
});
export const UpdateNotificationPlanInputSchema = z.object({
    title: z.string().trim().min(1).max(255).optional(),
    body: z.string().trim().min(1).optional(),
    channels: z.array(z.string()).optional(),
    scheduleConfig: z.record(z.string(), z.unknown()).optional(),
});
export const NotificationPlanResponseSchema = z.object({
    id: z.uuid(),
    contestId: z.uuid(),
    type: ContestNotificationTypeSchema,
    title: z.string(),
    body: z.string(),
    channels: z.array(z.string()),
    scheduleConfig: z.record(z.string(), z.unknown()),
    lastSentAt: z.iso.datetime().nullable(),
    nextSendAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ===== SCORE TREND SCHEMAS =====
/**
 * Input schema for the getScoreTrend endpoint.
 * Supports both aggregate (daily averages) and individual (per-entity snapshots) views.
 */
export const ScoreTrendInputSchema = z
    .object({
    contestId: z.uuid(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    view: z.enum(["aggregate", "individual"]).default("aggregate"),
})
    .superRefine((data, ctx) => {
    if (data.startDate && data.endDate) {
        const start = new Date(data.startDate);
        const end = new Date(data.endDate);
        if (end <= start) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "endDate must be after startDate",
                path: ["endDate"],
            });
        }
        const diffMs = end.getTime() - start.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays > 90) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Date range cannot exceed 90 days",
                path: ["endDate"],
            });
        }
    }
});
/**
 * Response for view=aggregate: daily average scores across all participants.
 */
export const ScoreTrendAggregateResponseSchema = z.object({
    contestId: z.uuid(),
    metric: ContestMetricSchema,
    scopeType: ContestScopeTypeSchema,
    view: z.literal("aggregate"),
    data: z.array(z.object({
        date: z.string().date(),
        averageScore: z.number(),
        participantCount: z.number(),
    })),
});
/**
 * Response for view=individual: per-entity time-series snapshots.
 */
export const ScoreTrendIndividualResponseSchema = z.object({
    contestId: z.uuid(),
    metric: ContestMetricSchema,
    scopeType: ContestScopeTypeSchema,
    view: z.literal("individual"),
    entries: z.array(z.object({
        entityId: z.uuid(),
        entityName: z.string(),
        snapshots: z.array(z.object({
            date: z.string().date(),
            computedScore: z.number(),
        })),
    })),
});
/**
 * Union response for getScoreTrend — discriminated by view field.
 */
export const ScoreTrendResponseSchema = z.discriminatedUnion("view", [
    ScoreTrendAggregateResponseSchema,
    ScoreTrendIndividualResponseSchema,
]);
